
import React, { useState, useMemo } from 'react';
import type { Suggestion, User, SuggestionArea } from '../types';
import PlusIcon from './icons/PlusIcon';
import PencilIcon from './icons/PencilIcon';
import TrashIcon from './icons/TrashIcon';
import SparklesIcon from './icons/SparklesIcon';
import { getTranslation } from '../utils/translations';

interface SuggestionModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    suggestions: Suggestion[];
    onAdd: (subject: string, text: string, area: SuggestionArea) => Promise<void>;
    onUpdate: (id: string, updates: Partial<Suggestion>) => Promise<void>;
}

const AREAS: SuggestionArea[] = ['recipes', 'shopping', 'planner', 'profile', 'household', 'other'];

const STATUS_COLORS: Record<string, string> = {
    new: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    denied: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    archive: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
    fulfilled: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
};

const SuggestionModal: React.FC<SuggestionModalProps> = ({ isOpen, onClose, user, suggestions, onAdd, onUpdate }) => {
    const t = (key: any) => getTranslation(user?.language || 'en', key);
    const [isAdding, setIsAdding] = useState(false);
    const [subject, setSubject] = useState('');
    const [text, setText] = useState('');
    const [area, setArea] = useState<SuggestionArea>('other');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    // Use safe active suggestions list
    const myActiveSuggestions = useMemo(() => 
        (suggestions || []).filter(s => s.status !== 'archive' && s.reporterUserId === user?.uid), 
    [suggestions, user?.uid]);

    if (!isOpen) return null;

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await onAdd(subject, text, area);
            setSubject('');
            setText('');
            setArea('other');
            setIsAdding(false);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingId) return;
        setLoading(true);
        try {
            await onUpdate(editingId, { subject, text, area });
            setEditingId(null);
            setSubject('');
            setText('');
            setArea('other');
            setIsAdding(false);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleArchive = async (id: string) => {
        if (window.confirm(t('archive') + '?')) {
            await onUpdate(id, { status: 'archive' });
        }
    };

    const startEdit = (s: Suggestion) => {
        setEditingId(s.id);
        setSubject(s.subject);
        setText(s.text);
        setArea(s.area);
        setIsAdding(true);
    };

    const handleCancel = () => {
        setIsAdding(false);
        setEditingId(null);
        setSubject('');
        setText('');
        setArea('other');
    };

    const getStatusColor = (status: string) => STATUS_COLORS[status] || STATUS_COLORS['new'];

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden transition-colors border border-white/10 dark:border-slate-800" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b flex justify-between items-center bg-slate-50 dark:bg-slate-800 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                        <div className="bg-amber-100 dark:bg-amber-900/20 p-2 rounded-xl text-amber-600 dark:text-amber-400">
                            <SparklesIcon className="h-6 w-6" />
                        </div>
                        <h2 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{t('suggestionBox')}</h2>
                    </div>
                    <button onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors p-2">&times;</button>
                </div>

                <div className="p-6 overflow-y-auto flex-grow bg-slate-50/50 dark:bg-slate-800/30">
                    {isAdding ? (
                        <form onSubmit={editingId ? handleUpdate : handleAdd} className="space-y-4 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm animate-fade-in">
                            <h3 className="font-black text-slate-700 dark:text-slate-200 mb-4 uppercase tracking-widest text-xs">
                                {editingId ? t('editRecipe') : t('addSuggestion')}
                            </h3>
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">{t('subject')}</label>
                                <input 
                                    type="text" required value={subject} onChange={e => setSubject(e.target.value)}
                                    className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none transition-all text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">{t('area')}</label>
                                    <select 
                                        value={area} onChange={e => setArea(e.target.value as SuggestionArea)}
                                        className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none transition-all text-sm font-bold text-slate-700 dark:text-slate-200 capitalize"
                                    >
                                        {AREAS.map(a => <option key={a} value={a} className="dark:bg-slate-800">{t(a)}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">{t('text')}</label>
                                <textarea 
                                    required value={text} onChange={e => setText(e.target.value)} rows={4}
                                    className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none transition-all resize-none text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button 
                                    type="button" onClick={handleCancel}
                                    className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                                >
                                    {t('cancel')}
                                </button>
                                <button 
                                    type="submit" disabled={loading}
                                    className="flex-1 py-3 bg-slate-900 dark:bg-slate-700 text-white font-black rounded-xl hover:bg-slate-800 dark:hover:bg-slate-600 transition-all shadow-lg disabled:opacity-50"
                                >
                                    {loading ? '...' : t('save')}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="space-y-4">
                            <button 
                                onClick={() => setIsAdding(true)}
                                className="w-full py-4 border-2 border-dashed border-amber-200 dark:border-amber-900/30 rounded-2xl text-amber-600 dark:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-all flex flex-col items-center justify-center gap-1 group"
                            >
                                <PlusIcon className="w-6 h-6 group-hover:rotate-90 transition-transform" />
                                <span className="font-black text-xs uppercase tracking-widest">{t('addSuggestion')}</span>
                            </button>

                            <div className="space-y-3">
                                <h3 className="font-black text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] px-1">{t('mySuggestions')}</h3>
                                {myActiveSuggestions.length === 0 ? (
                                    <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 border-dashed">
                                        <p className="text-slate-400 dark:text-slate-500 font-bold text-sm">{t('noResults')}</p>
                                    </div>
                                ) : (
                                    myActiveSuggestions.map(s => (
                                        <div key={s.id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-5 shadow-sm transition-all hover:border-amber-200 dark:hover:border-amber-800 group">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h4 className="font-black text-slate-800 dark:text-slate-100 leading-tight">{s.subject}</h4>
                                                    <span className="text-[10px] font-black uppercase text-slate-400 dark:text-slate-500 tracking-tighter capitalize">{t(s.area)}</span>
                                                </div>
                                                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${getStatusColor(s.status)}`}>
                                                    {t(s.status)}
                                                </span>
                                            </div>
                                            <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-4 leading-relaxed">{s.text}</p>
                                            <div className="flex justify-between items-center border-t border-slate-50 dark:border-slate-800 pt-4 mt-2">
                                                <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase">{new Date(s.date).toLocaleDateString()}</span>
                                                <div className="flex gap-1">
                                                    <button onClick={() => startEdit(s)} className="p-2 text-slate-300 dark:text-slate-600 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-all" title={t('editRecipe')}><PencilIcon className="w-4 h-4" /></button>
                                                    <button onClick={() => handleArchive(s.id)} className="p-2 text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all" title={t('archive')}><TrashIcon className="w-4 h-4" /></button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-4 bg-slate-100 dark:bg-slate-800 border-t dark:border-slate-700 flex justify-end">
                    <button onClick={onClose} className="px-8 py-2.5 bg-slate-800 dark:bg-slate-700 text-white font-black rounded-xl hover:bg-slate-900 dark:hover:bg-slate-600 transition-all shadow-lg">{t('close')}</button>
                </div>
            </div>
        </div>
    );
};

export default SuggestionModal;
